import React, { useState } from "react"
import { t } from "ttag"
import { CenteredLoader, PrimaryButton } from "@humanpredictiveintelligence/myqvt-library"
import { ApolloError } from "@apollo/client"
import { Prompt, useHistory } from "react-router-dom"

import { NotificationContext } from "features/Notification"
import { withRouteGuard } from "features/Navigation/withRouteGuard"
import { APPLICATION_URL } from "features/Navigation"
import { UserPermissionCode } from "models/generated"
import { QuestionOnDemandDialog } from "../../../Shared"
import { CustomSurvey, SurveyDetails, ThemeQuestionWithTheme } from "../../models"
import { useCustomSurveyValidation } from "../../hooks"
import { evictSurveyFromCache } from "../../utils"
import {
  CancelChangesDialog,
  DeleteSurveyDialog,
  KillerQuestionSection,
  SurveyDetailsForm,
  SurveyQuestionsLibrary,
} from "./components"
import * as Styles from "./SurveyForm.styles"

import { useGetCustomSurveyLazyQuery } from "graphql/queries/generated/getCustomSurvey"
import { CreateSurveyMutationVariables, useCreateSurveyMutation } from "graphql/mutations/generated/CreateSurvey"
import { useUpdateSurveyMutation } from "graphql/mutations/generated/UpdateSurvey"
import { useDeleteSurveyMutation } from "graphql/mutations/generated/DeleteSurvey"
import { usePreviewSurveyMutation } from "graphql/mutations/generated/PreviewSurvey"

export const SurveyFormComponent: React.FC<SurveyFormProps> = (props) => {
  const notification = React.useContext(NotificationContext)
  const history = useHistory()

  const [ isPageLoading, setIsPageLoading ] = useState(true)
  const [ isCancelRequested, setIsCancelRequested ] = React.useState<boolean>(false)
  const [ isDeleteRequested, setIsDeleteRequested ] = React.useState<boolean>(false)
  const [ customSurveyForm, setCustomSurveyForm ] = React.useState<ICustomSurveyFormData>(buildEmptyForm())
  const [ customSurvey, setCustomSurvey ] = React.useState<CustomSurvey | undefined>()

  const {
    isValid,
    isDirty,
    fields,
    isSurveyImmutable,
    isSurveyDeletable,
    isSurveyUsed,
    isEditMode,
  } = useCustomSurveyValidation(customSurveyForm, customSurvey)

  const [ getCustomSurvey ] = useGetCustomSurveyLazyQuery()

  const [ createSurveyMutation, { loading: isSurveyBeingCreated } ] = useCreateSurveyMutation()
  const [ updateSurveyMutation, { loading: isSurveyBeingUpdated } ] = useUpdateSurveyMutation({
    update: (cache, mutation) => {
      mutation.data?.editSurvey.id && evictSurveyFromCache(cache, mutation.data.editSurvey.id)
    },
  })
  const [ deleteSurveyMutation, { loading: isSurveyBeingDeleted } ] = useDeleteSurveyMutation()
  const [ getPreview, { loading: isPreviewBeingCreated } ] = usePreviewSurveyMutation()

  React.useEffect(() => {
    if (props.surveyId) {
      fetchAndSetSurveyForm(props.surveyId)
    }
    else {
      setEmptyForm()
      setIsPageLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ props.surveyId ])

  if (isPageLoading) {
    return <CenteredLoader isTransparent/>
  }

  return (
    <Styles.Container>
      <Styles.FormSection>
        <Styles.Heading>
          {isEditMode ? t`Edit my survey` : t`Create a new survey`}
        </Styles.Heading>
        {isSurveyUsed && (
          <Styles.SurveyUsedWarning text={t`This survey is used and cannot be modified.`} variant="warning"/>
        )}
        <SurveyDetailsForm
          isReadOnly={isSurveyUsed}
          surveyDetails={customSurveyForm.surveyDetails}
          onChange={(surveyDetails) => updateSurveyForm({ surveyDetails })}
        />
        <KillerQuestionSection/>
        <SurveyQuestionsLibrary
          isReadOnly={isSurveyUsed}
          surveyQuestions={customSurveyForm.surveyQuestions}
          onChange={(surveyQuestions) => updateSurveyForm({ surveyQuestions })}
        />
      </Styles.FormSection>
      <Styles.BottomSection>
        <QuestionOnDemandDialog/>
        <Styles.FormActions>
          <Styles.Validators>
            {fields.map(field => (
              <Styles.Validator key={field.wording} $isValid={field.isValid}>{field.wording}</Styles.Validator>
            ))}
          </Styles.Validators>
          <Styles.Actions>
            {isEditMode && (
              <PrimaryButton
                destructive
                disabled={!isSurveyDeletable}
                tooltip={!isSurveyDeletable
                  ? t`The survey is already in use and cannot be deleted anymore.`
                  : undefined
                }
                onClick={() => setIsDeleteRequested(true)}
              >
                {t`Delete`}
              </PrimaryButton>
            )}
            <PrimaryButton
              destructive
              onClick={() => isDirty
                ? setIsCancelRequested(true)
                : props.onCancelRequested()
              }
            >
              {t`Cancel`}
            </PrimaryButton>
            <PrimaryButton
              onClick={() => getSurveyPreview(customSurveyForm)}
              tooltip={!isPreviewBeingCreated ? t`Open the preview of the survey in a new tab.` : undefined}
              disabled={!isValid}
              isLoading={isPreviewBeingCreated}
            >
              {t`Preview`}
            </PrimaryButton>
            <PrimaryButton
              onClick={() => upsertSurvey(customSurveyForm, props.surveyId)}
              isLoading={isSurveyBeingCreated || isSurveyBeingUpdated}
              disabled={isSurveyImmutable}
              tooltip={isSurveyUsed ? t`The survey is already in use and cannot be updated anymore.` : undefined}
            >
              {t`Save`}
            </PrimaryButton>
          </Styles.Actions>
        </Styles.FormActions>
      </Styles.BottomSection>

      <CancelChangesDialog
        isOpen={isCancelRequested}
        onConfirm={props.onCancelRequested}
        onCancel={() => setIsCancelRequested(false)}
      />

      {props.surveyId && (
        <DeleteSurveyDialog
          isOpen={isDeleteRequested}
          surveyId={props.surveyId}
          onConfirm={(id) => deleteSurvey(id)}
          isLoading={isSurveyBeingDeleted}
          onCancel={() => setIsDeleteRequested(false)}
        />
      )}

      <Prompt
        when={isDirty && !isCancelRequested}
        message={t`Your survey has unsaved changes. If you confirm, you will lose your changes.`}
      />
    </Styles.Container>
  )

  function updateSurveyForm(surveyFormData: Partial<ICustomSurveyFormData>): void {
    setCustomSurveyForm(currentForm => ({ ...currentForm, ...surveyFormData }))
  }

  async function fetchAndSetSurveyForm(surveyId: number): Promise<void> {
    setIsPageLoading(true)

    const { data: surveyData } = await getCustomSurvey({ variables: { surveyId } })

    if (!surveyData) {
      notification.error(t`An error occurred when fetching the survey`)
      history.push(APPLICATION_URL.speakupSurveys())
      return
    }

    setCustomSurvey(surveyData.survey)
    setCustomSurveyForm(buildForm(surveyData.survey))

    setIsPageLoading(false)
  }

  function setEmptyForm(): void {
    setCustomSurvey(undefined)

    const form = buildEmptyForm()
    setCustomSurveyForm(form)
  }

  function buildForm(surveyData: CustomSurvey): ICustomSurveyFormData {
    return {
      surveyDetails: buildDetailsFromSurvey(surveyData),
      surveyQuestions: surveyData.questions,
    }
  }

  function buildEmptyForm(): ICustomSurveyFormData {
    return {
      surveyDetails: buildDetailsFromSurvey(),
      surveyQuestions: [],
    }
  }

  function buildDetailsFromSurvey(survey?: CustomSurvey): SurveyDetails {
    if (!survey) {
      return {
        description: "",
        name: "",
      }
    }

    return {
      description: survey.description,
      name: survey.name,
    }
  }

  async function upsertSurvey(surveyForm: ICustomSurveyFormData, surveyId?: number): Promise<void> {
    const variables = buildSurveyData(surveyForm)

    if (surveyId) {
      try {
        await updateSurveyMutation({
          variables: {
            ...variables,
            surveyId,
          },
        })
        notification.success(t`The survey was successfully updated`)
      }
      catch (error) {
        handleErrors(error, t`An error occurred during the survey update`)
      }
    }
    else {
      try {
        await createSurveyMutation({ variables })
        notification.success(t`The survey was successfully created`)
      }
      catch (error) {
        handleErrors(error, t`An error occurred during the survey creation`)
      }
    }

    setCustomSurveyForm(buildEmptyForm())
    setCustomSurvey(undefined)
    props.onSurveyCUD()
  }

  async function getSurveyPreview(surveyForm: ICustomSurveyFormData): Promise<void> {
    try {
      const { data } = await getPreview({
        variables: buildSurveyData(surveyForm),
      })

      if (!data?.previewSurvey) {
        throw new Error("No survey preview available")
      }

      window.open(data.previewSurvey, "_blank")
    }
    catch (error) {
      handleErrors(error, t`An error occurred during the preview creation`)
    }
  }

  function buildSurveyData(surveyForm: ICustomSurveyFormData): CreateSurveyMutationVariables {
    const questionIds = surveyForm.surveyQuestions.map((question) => question.id)

    return {
      description: surveyForm.surveyDetails.description,
      name: surveyForm.surveyDetails.name,
      questionIds,
    }
  }

  async function deleteSurvey(id: number): Promise<void> {
    try {
      await deleteSurveyMutation({ variables: { id } })
      notification.success(t`The survey was successfully deleted`)
      props.onSurveyCUD()
      setIsDeleteRequested(false)
    }
    catch (error) {
      notification.error(t`An error occurred while deleting the survey`)
    }
  }

  function handleErrors(error: unknown, fallbackError?: string): void {
    if (error instanceof ApolloError && error.graphQLErrors?.[0]) {
      notification.error(error.graphQLErrors?.[0].message)
    }
    else if (fallbackError) {
      notification.error(fallbackError)
    }

    throw error
  }
}

export interface ICustomSurveyFormData {
  surveyDetails: SurveyDetails,
  surveyQuestions: ThemeQuestionWithTheme[],
}

interface SurveyFormProps {
  surveyId?: number,
  onSurveyCUD: () => void,
  onCancelRequested: () => void,
}

export const SurveyForm = withRouteGuard(
  SurveyFormComponent,
  APPLICATION_URL.speakupSurveys(),
  (_, isUserAllowed) => !isUserAllowed(UserPermissionCode.SurveyCreate),
)
